import React, { createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';

import NotificationModal from '../../../../components/Modals/ReportErrorModal';

import {
  getRegistrationAccount,
  verifyRegistration,
} from '../../../../services/RegistrationService';
import { registrationForms, defaultErrorMessage, defaultAccountData } from './defaults';
import {
  hasError,
  validateAccount,
  validateAssociation,
  validateCustomerAccountAgreement,
  validateDisclosures,
  validateEmployment,
  validateIdentification,
  validateObjective,
  validateTrading,
  validateTrustedContact,
} from './validation';
import { getArrayKeyIndex } from '../../../../lib/utils/utils';
import { formatPbDate } from '../../../../lib/fmt';
import { dateFieldFormat } from '../../../../lib/format/format';
import { listSystemCode } from '../../../../services/CommonService';
import { fetchDisclosures } from '../../../../services/DisclosureService';
import { getCorpAttachments } from '../../../../services/RegistrationService';
import { getCurrentUser } from '../../../../services/AuthService';

const accountContext = createContext(defaultAccountData);

export const AccountContextProvider = ({ children }) => {
  const [account, setAccount] = React.useState(defaultAccountData);
  const [investmentObjectives, setInvestmentObjectives] = React.useState([]);
  const [dynamicDisclosures, setDynamicDisclosures] = React.useState([]);
  const [dynamicCompanyAttachments, setDynamicCompanyAttachments] = React.useState([]);

  const [isLoading, setIsLoading] = React.useState(false);
  const [forms, setForms] = React.useState([...registrationForms]);
  const [errorMessage, setErrorMessage] = React.useState(defaultErrorMessage);
  const { key: verificationKey, accountId } = useParams();

  React.useEffect(() => {
    initialize();
    // eslint-disable-next-line
  }, [account.accountId]);

  const initialize = async () => {
    if (account.accountId) {
      const disclosuresList = await getDynamicDisclosures();
      const investmentObjectivesList = await getInvestmentObjectives();
      const companyAttachmentsList = await getCompanyAttachments(account.accountId);
      await updateCompletedForms(
        disclosuresList,
        investmentObjectivesList,
        companyAttachmentsList
      );
    } else {
      getAccount();
    }
  };

  const setAccountResponse = (resAcct) => {
    // FORMAT IDENTIFICATION
    resAcct.primaryOwner.identification = setOwnerIdentificationResponse(
      resAcct.primaryOwner.identification,
      resAcct.primaryOwner.country
    );

    // corp owner identifications
    for (let index = 0; index < resAcct?.corpOwnersList?.length || 0; index++) {
      resAcct.corpOwnersList[index].identification = setOwnerIdentificationResponse(
        resAcct.corpOwnersList[index].identification,
        resAcct.corpOwnersList[index].country
      );
    }

    // trusted contact
    const onboardingPercent = parseInt(resAcct?.onboardingPercent || '0');
    if (resAcct?.trustedContact?.ownerId) {
      resAcct.trustedContact.haveTrustedContact = 'Yes';
    } else if (
      onboardingPercent >= 100 ||
      ['7', '8'].includes((resAcct?.onboardingPercent || '').charAt(1))
    ) {
      resAcct.trustedContact.haveTrustedContact = 'No';
    }

    if (
      resAcct?.trustedContact?.relationship &&
      resAcct?.trustedContact?.relationship.includes('Other') &&
      !resAcct.trustedContact.other
    ) {
      resAcct.trustedContact.other = resAcct.trustedContact.relationship.substring(
        6,
        resAcct.trustedContact.relationship.length
      );

      resAcct.trustedContact.relationship = 'Other';
    }
    resAcct.trustedContact.dateOfBirth = dateFieldFormat(
      formatPbDate(resAcct.trustedContact.dateOfBirth)
    );

    // association
    if (typeof resAcct.primaryOwner.association.brokerMember === 'boolean') {
      resAcct.primaryOwner.association.brokerMember = resAcct.primaryOwner.association
        .brokerMember
        ? 'Yes'
        : 'No';
    }

    if (typeof resAcct.primaryOwner.association.ownership === 'boolean') {
      resAcct.primaryOwner.association.ownership = resAcct.primaryOwner.association
        .ownership
        ? 'Yes'
        : 'No';
    }

    setAccount(resAcct);

    console.log('account response', resAcct);
  };

  const getDynamicDisclosures = async () => {
    try {
      const { disclosuresList } = await fetchDisclosures();
      setDynamicDisclosures(disclosuresList);

      return disclosuresList;
    } catch (error) {
      console.error('get dynamic disclosures', error);
    }

    return [];
  };

  const getCompanyAttachments = async (accountId) => {
    try {
      const { corpAttachmentsList } = await getCorpAttachments(accountId);
      setDynamicCompanyAttachments(corpAttachmentsList);

      return corpAttachmentsList;
    } catch (error) {
      console.error('get company attachments', error);
    }

    return [];
  };

  const getInvestmentObjectives = async () => {
    const objectivesResp = await listSystemCode({
      type: 'Investment Objective',
      subType: '',
    });

    setInvestmentObjectives(objectivesResp.systemCodeList);

    return objectivesResp.systemCodeList;
  };

  const setOwnerIdentificationResponse = (id, defaultCountry) => {
    if (typeof id.issueDate === 'object') {
      id.issueDate = dateFieldFormat(formatPbDate(id.issueDate));
    }
    if (typeof id.dateOfBirth === 'object') {
      id.dateOfBirth = dateFieldFormat(formatPbDate(id.dateOfBirth));
    }
    if (typeof id.expirationDate === 'object') {
      id.expirationDate = dateFieldFormat(formatPbDate(id.expirationDate));
    }

    id.isSameCitizenship = !id.citizenship ? true : id.citizenship === defaultCountry;

    id.isSameTaxCountry = !id.taxCountry ? true : id.taxCountry === defaultCountry;

    id.isSameCountryOfIssuance = !id.countryOfIssuance
      ? true
      : id.countryOfIssuance === defaultCountry;

    return id;
  };

  const getAccount = async () => {
    try {
      setIsLoading(true);
      let resAcct = {};
      if (!verificationKey) {
        resAcct = await getRegistrationAccount(accountId);
      } else {
        resAcct = await verifyRegistration(verificationKey);
      }

      setAccountResponse(resAcct);
    } catch (error) {
      console.error(error);
      return setErrorMessage({
        title: 'Ooopss...',
        msg: 'Unable to get registration details.',
        errMsg: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getFormIndex = (formName) => {
    return getArrayKeyIndex(forms, formName, 'name');
  };

  const updateCompletedForms = async (
    disclosuresList,
    investmentObjectivesList,
    companyAttachmentsList
  ) => {
    const onboardingPercent = parseInt(account?.onboardingPercent || 0);
    const formsCopy = [...forms];

    if (
      !hasError(validateAccount(account, isBussinessAccount(), companyAttachmentsList))
    ) {
      formsCopy[getFormIndex('account')].isCompleted = true;
    }

    if (
      !hasError(
        validateIdentification(account.primaryOwner.identification, account.primaryOwner)
      )
    ) {
      formsCopy[getFormIndex('identification')].isCompleted = true;
    }

    for (let index = 0; index < account.corpOwnersList?.length; index++) {
      const coOwner = account.corpOwnersList[index];

      if (coOwner.isOwner && parseInt(coOwner.ownershipPercent) >= 25) {
        if (
          hasError(
            validateIdentification(
              account.corpOwnersList[index]?.identification,
              account.corpOwnersList[index]
            )
          )
        ) {
          formsCopy[getFormIndex('identification')].isCompleted = false;
          break;
        }
      }
    }

    if (!hasError(validateCustomerAccountAgreement(account))) {
      formsCopy[getFormIndex('customer-account-agreement')].isCompleted = true;
    }

    if (!hasError(validateTrustedContact(account.trustedContact))) {
      formsCopy[getFormIndex('trusted-contact')].isCompleted = true;
    } else if (
      onboardingPercent >= 100 ||
      ['7', '8'].includes((account?.onboardingPercent || '').charAt(1))
    ) {
      formsCopy[getFormIndex('trusted-contact')].isCompleted = true;
    }

    if (!hasError(validateEmployment(account.primaryOwner.employment))) {
      formsCopy[getFormIndex('employment')].isCompleted = true;
    }

    if (!hasError(validateAssociation(account.primaryOwner.association))) {
      if (
        account.primaryOwner.association.ownership === 'No' &&
        account.primaryOwner.association.brokerMember === 'No'
      ) {
        if (
          onboardingPercent >= 100 ||
          ['7', '9'].includes((account?.onboardingPercent || '').charAt(1))
        ) {
          formsCopy[getFormIndex('association')].isCompleted = true;
        }
      } else {
        formsCopy[getFormIndex('association')].isCompleted = true;
      }
    }

    if (!hasError(validateTrading(account))) {
      formsCopy[getFormIndex('trading')].isCompleted = true;
    }

    if (!hasError(validateObjective(account.objective, investmentObjectivesList))) {
      formsCopy[getFormIndex('objective')].isCompleted = true;
    }

    if (!hasError(validateDisclosures(account, disclosuresList))) {
      formsCopy[getFormIndex('disclosures')].isCompleted = true;
    }

    if (onboardingPercent >= 100) {
      formsCopy[getFormIndex('submit')].isCompleted = true;
    }

    setForms(formsCopy);
  };

  // corporation / Individual Retirement Account
  const isBussinessAccount = () => {
    return ['C', 'IRA'].includes(account.accountType);
  };

  const viewOnly = account?.userId !== getCurrentUser()?.UserId;
  const disableUpdate = account?.status !== 'In Progress' || viewOnly;

  return (
    <accountContext.Provider
      value={{
        account,
        setAccount,
        isBussinessAccount,
        forms,
        isLoading,
        setForms,
        getFormIndex,
        setIsLoading,
        setAccountResponse,
        investmentObjectives,
        dynamicDisclosures,
        setDynamicDisclosures,
        dynamicCompanyAttachments,
        setDynamicCompanyAttachments,
        setErrorMessage,
        disableUpdate,
        viewOnly,
      }}
    >
      {children}
      <NotificationModal
        open={!!errorMessage.msg}
        message={errorMessage.msg}
        title={errorMessage.title}
        errorMessage={errorMessage.errMsg}
        onClose={() => {
          setErrorMessage(defaultErrorMessage);
          window.location = '/login';
        }}
      />
    </accountContext.Provider>
  );
};

// custom hook to use the accountContext and access global application data
export const useAccountContext = () => useContext(accountContext);
